import React, { useState } from 'react';
import styles from './ManagerCertificatesListItem.module.scss';
import FormattedMomentDate, {
	DateFormat,
} from '../../../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import Text from '../../../../../../Components/Base/Text/Text';
import { Theme } from '../../../../../../Enums/Theme.enum';
import useTranslation from '../../../../../../Hooks/useTranslation';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import OverlayLoading from '../../../../../../Components/OverlayLoading';
import { WorkplaceCertificate } from '../../../../../../graphql/graphql';
import ManagerCertificateUploadWizardModal from '../ManagerCertificateUpload/ManagerCertificateUploadWizardModal';
import { CertificateType } from '../../../../Enums/CertificateType.enum';
import LinkButton from '../../../../../../Components/Button/LinkButton';

interface Props {
	item: WorkplaceCertificate;
	onReload: () => void;
	workplaceId: string;
}

const ManagerCertificatesListItem: React.FC<Props> = ({
	item,
	onReload,
	workplaceId,
}) => {
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const [isVerificationLoading, setIsVerificationLoading] = useState(false);
	const [isUploadCertificateOpen, setIsUploadCertificateOpen] = useState(false);

	const handleVerifySUKLCertificate = async () => {
		setIsVerificationLoading(true);
		try {
			const response = await apiClient.get('/sukl/test-cert?source=erecept');
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('component.errorMessages.connectionSuccessful'),
				});
			}
		} catch (e) {
			displayError(e);
		}
		setIsVerificationLoading(false);
	};

	const handleFinishCertificateUpload = () => {
		showNotificationSuccess({
			content: t('component.certificates.certificateSaved'),
		});
		setIsUploadCertificateOpen(false);
		onReload();
	};

	const handleOnDeleteCertificate = () => {
		setIsUploadCertificateOpen(false);
		onReload();
	};

	return (
		<>
			<div className={styles.item}>
				<div className={styles.content}>
					<div>
						{item.certificate?.isExpired ? (
							<div className={styles.expiredBall} />
						) : (
							<div className={styles.validBall} />
						)}
					</div>
					<div>
						<div className={styles.title}>
							{item.name}{' '}
							{item.certificate?.expires && (
								<span>
									({t('base.expirationDate')}{' '}
									<FormattedMomentDate
										date={item.certificate.expires}
										format={DateFormat.DATE}
									/>
									)
								</span>
							)}
						</div>
						{item.description && (
							<Text
								className={styles.description}
								tx={item.description}
								firstUpper
							/>
						)}
					</div>
				</div>
				<div className={styles.controls}>
					{item.certificate?.type === CertificateType.SUKL && (
						<LinkButton
							theme={Theme.BLUE}
							title='component.signature.verifyPassword'
							onClick={handleVerifySUKLCertificate}
							disabled={isVerificationLoading}
							firstUpper
						/>
					)}
					<LinkButton
						theme={item.certificate ? Theme.BLUE : Theme.GREEN}
						title={
							item.certificate
								? 'base.shared.edit'
								: 'component.certificates.uploadCertificate'
						}
						onClick={() => setIsUploadCertificateOpen(true)}
						firstUpper
					/>
				</div>
			</div>
			{isVerificationLoading && <OverlayLoading />}
			{isUploadCertificateOpen && (
				<ManagerCertificateUploadWizardModal
					isOpen
					onClose={() => setIsUploadCertificateOpen(false)}
					onDelete={handleOnDeleteCertificate}
					onFinish={handleFinishCertificateUpload}
					certificateType={item.type}
					certificateId={item.certificate?.id ?? undefined}
					workplaceId={workplaceId}
				/>
			)}
		</>
	);
};

export default ManagerCertificatesListItem;
